import React, { useCallback, useContext, useMemo, useState } from 'react'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import Heading from 'LuxKit/Typography/Heading'
import LePackageUpgradeCardRoomDetails from './LePackageUpgradeCardRoomDetails'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import ResponsiveImage, { ImageParams } from 'components/Common/ResponsiveImage/ResponsiveImage'
import Divider from 'LuxKit/Divider'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Expander from 'components/utils/Expander'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import styled from 'styled-components'
import LePackageUpgradePrice from './LePackageUpgradePrice'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { useAppSelector } from 'hooks/reduxHooks'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import ModalBase from 'LuxKit/Modal/ModalBase'
import Breakpoint from 'components/Common/Breakpoint'
import PackageUpgradeSurchargeMarginComparisonText from './PackageUpgradeSurchargeMarginTexts/PackageUpgradeSurchargeMarginComparisonText'
import { connect } from 'react-redux'
import Plural from 'components/utils/Formatters/Plural'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import PriceRowLoyaltyPoints from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPoints'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import PriceRowLoyaltyPointsLoadingSkeleton from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPointsLoadingSkeleton'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import LoyaltyGenericInfoModal from 'loyalty/components/LoyaltyGenericInfoModal'
import ModalContext from 'contexts/ModalContext'
import * as Analytics from 'analytics/analytics'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'

const StyledSlideDown = styled(Expander)`
  &.closed {
    margin-top: 0;
  }
`

const imageProps: ImageParams = {
  mobileAspectRatio: '11:5',
  tabletAspectRatio: '22:15',
  tabletWidth: '200px',
  quality: 'good',
}

interface MappedProps {
  isSpoofed: boolean
  isStoreMode: boolean
  isLoyaltyEnabled: boolean
}

export interface PackageOptionUpgradeView {
  offer: App.Offer;
  hasFlightPrices?: boolean;
  package: App.Package;
  capacityValid?: boolean;
  isAvailable?: boolean;
  price: number;
  memberPrice: number;
  priceDifference: number;
  memberPriceDifference: number;
  isSelected?: boolean;
  withFlights?: boolean;
  value?: number;
  flightPrice?: number;
  flightsAvailable?: boolean;
}

interface Props {
  offer: App.Offer;
  upgradePackageView: OfferPackageOptionView;
  basePackageView: OfferPackageOptionView;
  checkIn?: string;
  hasFlights?: boolean;
}

function PackageUpgradeModal(props: Props & MappedProps) {
  const { offer, upgradePackageView, basePackageView, hasFlights, checkIn, isSpoofed, isStoreMode, isLoyaltyEnabled } = props
  const modalContext = useModalElementContext<OfferPackageOptionView>()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const packageUpgrade = useMemo<PackageOptionUpgradeView>(() => ({
    offer,
    // no need to account for flight prices, they are always the same for the same day
    priceDifference: upgradePackageView.hotelPrice - basePackageView.hotelPrice,
    memberPriceDifference: upgradePackageView.hotelMemberPrice - basePackageView.hotelMemberPrice,
    price: upgradePackageView.hotelPrice,
    memberPrice: upgradePackageView.hotelMemberPrice,
    package: upgradePackageView.package,
    value: upgradePackageView.hotelValue,
    flightPrice: upgradePackageView.flightPrice,
    flightsAvailable: upgradePackageView.flightsAvailable,
  }), [basePackageView, upgradePackageView, offer])

  const onUpgradeClick = useCallback(() => {
    trackEvent('UPGRADE_MODAL_ACCEPTED')
    Analytics.trackClientEvent({
      subject: 'upgrade_modal',
      action: 'select_upgrade',
      category: 'logging',
      type: 'interaction',
    })
    modalContext.resolve(upgradePackageView)
  }, [modalContext, trackEvent, upgradePackageView])

  const onNoThanksClick = useCallback(() => {
    trackEvent('UPGRADE_MODAL_REJECTED')
    Analytics.trackClientEvent({
      subject: 'upgrade_modal',
      action: 'no_upgrade',
      category: 'logging',
      type: 'interaction',
    })
    modalContext.resolve(basePackageView)
  }, [basePackageView, modalContext, trackEvent])

  const name = packageUpgrade.package.name
  const roomType = packageUpgrade.package.roomType
  const firstRoomImageId = packageUpgrade.package.roomType?.images?.[0]?.id

  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(packageUpgrade.package)

  const showLuxPlusInclusions = !!luxPlusInclusions.length

  const [showDetails, setShowDetails] = useState(false)
  const onViewDetails = useCallback(() => {
    setShowDetails(!showDetails)
  }, [showDetails])

  const { currentRegionCode } = useContext(GeoContext)
  const currency = useContext(CurrencyContext)

  const basePackagePointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, {
      packageView: basePackageView,
    }),
  ], [offer, basePackageView])

  const upgradePackagePointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, {
      packageView: upgradePackageView,
    }),
  ], [offer, upgradePackageView])

  const { totalPointsCalculation: basePackagePointsEarnCalculation } = useLoyaltyPointsEarnCalculator(basePackagePointsEarnCalculationRequests)
  const { totalPointsCalculation: upgradePackagePointsEarnCalculation } = useLoyaltyPointsEarnCalculator(upgradePackagePointsEarnCalculationRequests)

  const anyPointsEarnFetching = basePackagePointsEarnCalculation.fetching || upgradePackagePointsEarnCalculation.fetching
  const anyPointsEarnError = basePackagePointsEarnCalculation.error || upgradePackagePointsEarnCalculation.error

  const basePackagePoints = basePackagePointsEarnCalculation.data?.points ?? 0
  const basePackageStatusCredits = basePackagePointsEarnCalculation.data?.statusCredits ?? 0

  const upgradePackagePoints = upgradePackagePointsEarnCalculation.data?.points ?? 0
  const upgradePackageStatusCredits = upgradePackagePointsEarnCalculation.data?.statusCredits ?? 0

  const upgradeAdditionalPointsEarn = upgradePackagePoints - basePackagePoints
  const upgradeAdditionalStatusCreditsEarn = upgradePackageStatusCredits - basePackageStatusCredits

  const displayTier = useAppSelector(getLoyaltyDisplayTier)

  const title = `Upgrade your room for only ${formatCurrency(packageUpgrade.memberPriceDifference || packageUpgrade.priceDifference, currency, currentRegionCode, { signDisplay: 'always' })} extra`
  const subtitle = ''
  const isDismissible = isSpoofed

  const showModal = useContext(ModalContext)
  const loyaltyInfoIconClick = useCallback(() => {
    showModal(<LoyaltyGenericInfoModal />)
  }, [showModal])

  return (
    <ModalBase size="L" height="full" tabletHeight="auto" dismissible={isDismissible}>
      <ModalHeader
        dismissible={isDismissible}
        title={title}
        subtitle={subtitle}
      />
      <ModalBody>
        <Breakpoint max="mobile">
          <div>
            {!!roomType?.images.length && <ImageGallery
              images={roomType?.images}
              imageParams={imageProps}
            />}
          </div>
        </Breakpoint>
        <ModalContent>
          <Group direction="vertical" gap={12}>
            {isSpoofed && !isStoreMode && <PackageUpgradeSurchargeMarginComparisonText basePackage={basePackageView.package} upgradePackage={upgradePackageView.package} checkIn={checkIn} />}
            <VerticalSpacer gap={12}>
              <CSSBreakpoint min="tablet">
                <VerticalSpacer gap={12}>
                  <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
                    <VerticalSpacer gap={12}>
                      <Heading variant="heading4">{name}</Heading>
                      {roomType?.sizeSqm && <BodyText variant="small"><b>Room size:</b> {roomType.sizeSqm}m&sup2;</BodyText>}
                      <LePackageUpgradePrice packageUpgrade={packageUpgrade} hasFlightsSelected={hasFlights} />
                      {isLoyaltyEnabled && <>
                        {anyPointsEarnFetching && <PriceRowLoyaltyPointsLoadingSkeleton tier={displayTier} size="S" />}
                        {!anyPointsEarnError && !anyPointsEarnFetching && upgradeAdditionalPointsEarn > 0 && <PriceRowLoyaltyPoints
                          tier={displayTier}
                          earnablePoints={upgradeAdditionalPointsEarn}
                          statusCredits={upgradeAdditionalStatusCreditsEarn}
                          size="S"
                          calculationType="additional"
                          onInfoIconClick={loyaltyInfoIconClick}
                        />}
                      </>}
                    </VerticalSpacer>
                    {firstRoomImageId && <ResponsiveImage
                      {...imageProps}
                      id={firstRoomImageId}
                      alt={`${roomType?.name} image 1 at ${offer.property?.name} by ${offer.property?.geoData.administrativeAreaLevel2}, ${offer.property?.geoData.administrativeAreaLevel1}, ${offer.property?.geoData.country}`}
                    />}
                  </Group>
                </VerticalSpacer>
              </CSSBreakpoint>
              <CSSBreakpoint max="mobile">
                <VerticalSpacer gap={12}>
                  <Heading variant="heading4">{name}</Heading>
                  <LePackageUpgradePrice packageUpgrade={packageUpgrade} hasFlightsSelected={hasFlights} />
                  {isLoyaltyEnabled && <>
                    {anyPointsEarnFetching && <PriceRowLoyaltyPointsLoadingSkeleton tier={displayTier} size="S" />}
                    {!anyPointsEarnError && !anyPointsEarnFetching && upgradeAdditionalPointsEarn > 0 && <PriceRowLoyaltyPoints
                      tier={displayTier}
                      earnablePoints={upgradeAdditionalPointsEarn}
                      statusCredits={upgradeAdditionalStatusCreditsEarn}
                      size="S"
                      calculationType="additional"
                      onInfoIconClick={loyaltyInfoIconClick}
                    />}
                  </>}
                  {roomType?.sizeSqm && <BodyText variant="small"><b>Room size:</b> {roomType.sizeSqm}m&sup2;</BodyText>}
                </VerticalSpacer>
              </CSSBreakpoint>
            </VerticalSpacer>
            <CSSBreakpoint min="tablet">
              <Divider kind="primary" />
            </CSSBreakpoint>

            <VerticalSpacer gap={16}>
              {showLuxPlusInclusions && <LuxPlusInclusions
                luxPlusInclusions={luxPlusInclusions}
                accommodationOffer={offer}
              />}
              {(!!inclusions.length || !!bonusInclusions.length) && <Group direction="vertical" gap={12}>
                <BodyText variant="small" weight="bold">
                  <Plural singular="inclusion" count={inclusions.length + bonusInclusions.length} withCount />:
                </BodyText>
                <InclusionsSection inclusions={inclusions} bonusInclusions={bonusInclusions} size="small" />
              </Group>}
              {roomType && <StyledSlideDown expanded={showDetails} className={showDetails ? '' : 'closed'}>
                <LePackageUpgradeCardRoomDetails roomType={roomType} />
              </StyledSlideDown>}
              <SmallToggleButton size="caption" on={showDetails} onClick={onViewDetails}>
                {showDetails ? 'Hide' : 'View'} more details
              </SmallToggleButton>
            </VerticalSpacer>
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          onClick: onUpgradeClick,
          children: 'Upgrade',
        }}
        secondaryActionProps={{
          onClick: onNoThanksClick,
          children: 'No thanks',
        }}
      />
    </ModalBase>
  )
}

function mapStateToProps(state: App.State): MappedProps {
  return {
    isSpoofed: state.auth.account.isSpoofed,
    isStoreMode: state.system.storeMode,
    isLoyaltyEnabled: getIsLoyaltyEnabled(state),
  }
}

export default connect(mapStateToProps)(PackageUpgradeModal)
